import { baseApi } from '@/shared/api/baseApi';
import { credentialsUpdated, sessionCleared } from '@/shared/lib/redux/actions';
import type { AuthResponse, MembershipSummary, SessionSummary, UserProfile } from '../model/types';

export interface RegisterRequest {
  email?: string;
  phone?: string;
  password: string;
  firstName: string;
  lastName: string;
  middleName?: string;
  businessName?: string;
}

export interface LoginRequest {
  identifier: string;
  password: string;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ForgotPasswordRequest {
  identifier: string;
}

export interface ResetPasswordRequest {
  identifier: string;
  code: string;
  newPassword: string;
}

export type VerifyPurpose = 'email' | 'phone';

export interface UpdateMeRequest {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  businessName?: string;
  locale?: string;
}

export interface MessageResponse {
  message: string;
}

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    register: builder.mutation<AuthResponse, RegisterRequest>({
      query: (body) => ({ url: 'auth/register', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(credentialsUpdated(data));
      },
    }),
    login: builder.mutation<AuthResponse, LoginRequest>({
      query: (body) => ({ url: 'auth/login', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        dispatch(credentialsUpdated(data));
      },
      invalidatesTags: ['Me', 'Memberships'],
    }),
    /** Bootstrap-only (docs/PLAN.md §6) — the httpOnly `rt` cookie carries the session; a normal
     *  401-triggered refresh happens inside baseApi's own reauth wrapper, not through this hook. */
    refresh: builder.mutation<AuthResponse, void>({
      query: () => ({ url: 'auth/refresh', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          dispatch(credentialsUpdated(data));
        } catch {
          dispatch(sessionCleared());
        }
      },
    }),
    logout: builder.mutation<MessageResponse, void>({
      query: () => ({ url: 'auth/logout', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(sessionCleared());
      },
    }),
    logoutAll: builder.mutation<MessageResponse, void>({
      query: () => ({ url: 'auth/logout-all', method: 'POST' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(sessionCleared());
      },
    }),
    changePassword: builder.mutation<MessageResponse, ChangePasswordRequest>({
      query: (body) => ({ url: 'auth/change-password', method: 'POST', body }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await queryFulfilled.catch(() => undefined);
        dispatch(sessionCleared());
      },
    }),
    forgotPassword: builder.mutation<MessageResponse, ForgotPasswordRequest>({
      query: (body) => ({ url: 'auth/forgot-password', method: 'POST', body }),
    }),
    resetPassword: builder.mutation<MessageResponse, ResetPasswordRequest>({
      query: (body) => ({ url: 'auth/reset-password', method: 'POST', body }),
    }),
    requestVerification: builder.mutation<MessageResponse, VerifyPurpose>({
      query: (purpose) => ({ url: 'auth/verify/request', method: 'POST', body: { purpose } }),
    }),
    confirmVerification: builder.mutation<
      MessageResponse,
      { purpose: VerifyPurpose; code: string }
    >({
      query: (body) => ({ url: 'auth/verify/confirm', method: 'POST', body }),
      invalidatesTags: ['Me'],
    }),

    getMe: builder.query<UserProfile, void>({
      query: () => 'me',
      providesTags: ['Me'],
    }),
    updateMe: builder.mutation<UserProfile, UpdateMeRequest>({
      query: (body) => ({ url: 'me', method: 'PATCH', body }),
      invalidatesTags: ['Me'],
    }),
    deleteMe: builder.mutation<void, void>({
      query: () => ({ url: 'me', method: 'DELETE' }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        // Unlike logout, a failure here (e.g. `OWNER_CANNOT_DELETE_ACCOUNT` for a store owner) is a real
        // business-rule rejection — only clear the session once the delete actually succeeded.
        try {
          await queryFulfilled;
          dispatch(sessionCleared());
        } catch {
          // handled by the caller's .unwrap().catch(...) — nothing to clear.
        }
      },
    }),
    listSessions: builder.query<SessionSummary[], void>({
      query: () => 'me/sessions',
    }),
    revokeSession: builder.mutation<void, string>({
      query: (id) => ({ url: `me/sessions/${id}`, method: 'DELETE' }),
    }),
    listMemberships: builder.query<MembershipSummary[], void>({
      query: () => 'me/memberships',
      providesTags: ['Memberships'],
    }),
  }),
});

export const {
  useRegisterMutation,
  useLoginMutation,
  useRefreshMutation,
  useLogoutMutation,
  useLogoutAllMutation,
  useChangePasswordMutation,
  useForgotPasswordMutation,
  useResetPasswordMutation,
  useRequestVerificationMutation,
  useConfirmVerificationMutation,
  useGetMeQuery,
  useLazyGetMeQuery,
  useUpdateMeMutation,
  useDeleteMeMutation,
  useListSessionsQuery,
  useRevokeSessionMutation,
  useListMembershipsQuery,
  useLazyListMembershipsQuery,
} = authApi;

export { authApi };
